/**
 * Centralized text-to-speech playback via the server-side OpenAI TTS proxy
 * (/api/voice/tts → gpt-4o-mini-tts).
 *
 * Streaming strategy: the server pipes MP3 chunks straight from OpenAI to us,
 * and we use the MediaSource API to feed those chunks into an <audio>
 * element. Playback begins as soon as enough bytes have arrived for the
 * decoder to start — typically 300-600 ms after click. The old `await
 * response.blob()` path forced us to wait for the full MP3 (4-5 s for a
 * paragraph), which is what made the read-aloud feel laggy.
 *
 * Browsers without MediaSource (rare) fall back to the original buffered
 * playback path so the feature still works.
 *
 * Module-level state ensures that calling playText() while another playback
 * is still active stops the old one — assistant messages never overlap.
 */

let currentAudio = null;
let currentAbort = null;
let currentUrl = null;
let currentMediaSource = null;

/**
 * Strip the markdown noise the TTS engine would say literally (asterisks,
 * code fences, link syntax) so we only read the prose to the user.
 */
function stripMarkdown(text) {
  return String(text || '')
    .replace(/`{1,3}[^`]*`{1,3}/g, ' ')
    .replace(/[*_#>~|]+/g, ' ')
    .replace(/\[([^\]]+)\]\([^)]*\)/g, '$1')
    .replace(/\s+/g, ' ')
    .trim();
}

/**
 * Speak the given text aloud. Resolves when playback begins (or skips, if
 * input is empty). Rejects on network / playback errors so the caller can
 * surface them.
 *
 * Path A (preferred): MediaSource — server streams MP3 chunks → we feed them
 *   into a SourceBuffer → audio starts playing as soon as enough decodable
 *   bytes arrive.
 * Path B (fallback): blob — for browsers without MediaSource support, fall
 *   back to the original buffered playback.
 */
export async function playText(text) {
  const plain = stripMarkdown(text);
  if (!plain) return;
  stopText();

  const ctl = new AbortController();
  currentAbort = ctl;

  const supportsMediaSource =
    typeof window !== 'undefined' &&
    typeof window.MediaSource !== 'undefined' &&
    window.MediaSource.isTypeSupported &&
    window.MediaSource.isTypeSupported('audio/mpeg');

  if (supportsMediaSource) {
    try {
      await playTextStreaming(plain, ctl);
      return;
    } catch (err) {
      if (err?.name === 'AbortError') throw err;
      console.warn('[voicePlayback] streaming path failed, falling back to buffered:', err?.message);
      // fall through to buffered
    }
  }

  // Buffered fallback (also serves browsers without MediaSource).
  try {
    const r = await fetch('/api/voice/tts', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ text: plain }),
      credentials: 'include',
      signal: ctl.signal,
    });
    if (!r.ok) throw new Error(`tts http ${r.status}`);
    const blob = await r.blob();
    const url = URL.createObjectURL(blob);
    currentUrl = url;
    const audio = new Audio(url);
    currentAudio = audio;
    audio.onended = () => {
      if (currentUrl === url) {
        URL.revokeObjectURL(url);
        currentUrl = null;
      }
      if (currentAudio === audio) currentAudio = null;
    };
    audio.onerror = () => {
      if (currentUrl === url) {
        URL.revokeObjectURL(url);
        currentUrl = null;
      }
      if (currentAudio === audio) currentAudio = null;
    };
    await audio.play();
  } catch (err) {
    if (err.name !== 'AbortError') {
      console.warn('[voicePlayback] failed:', err?.message);
    }
    throw err;
  } finally {
    if (currentAbort === ctl) currentAbort = null;
  }
}

/**
 * Stream MP3 chunks from /api/voice/tts and play them as they arrive using
 * MediaSource. Resolves when audio.play() succeeds (playback has started or
 * is queued); rejects on fetch / decode errors.
 */
async function playTextStreaming(plain, ctl) {
  const r = await fetch('/api/voice/tts', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ text: plain }),
    credentials: 'include',
    signal: ctl.signal,
  });
  if (!r.ok) throw new Error(`tts http ${r.status}`);
  if (!r.body) throw new Error('no response body');

  const mediaSource = new MediaSource();
  currentMediaSource = mediaSource;
  const url = URL.createObjectURL(mediaSource);
  currentUrl = url;
  const audio = new Audio(url);
  currentAudio = audio;
  audio.onended = () => {
    if (currentUrl === url) {
      URL.revokeObjectURL(url);
      currentUrl = null;
    }
    if (currentAudio === audio) currentAudio = null;
  };
  audio.onerror = () => {
    if (currentUrl === url) {
      URL.revokeObjectURL(url);
      currentUrl = null;
    }
    if (currentAudio === audio) currentAudio = null;
  };

  // Wait for the MediaSource to open, then start piping bytes into a
  // SourceBuffer. We start playback aggressively so audio begins the moment
  // the decoder has enough data — without waiting for the full stream.
  await new Promise((resolve, reject) => {
    let sourceBuffer;
    const reader = r.body.getReader();

    const onSourceOpen = async () => {
      try {
        sourceBuffer = mediaSource.addSourceBuffer('audio/mpeg');
        // Append chunks serially — appendBuffer is async (updateend signals
        // ready) and only one in-flight append is allowed per SourceBuffer.
        const pump = async () => {
          while (true) {
            const { done, value } = await reader.read();
            if (done) {
              try { mediaSource.endOfStream(); } catch (_) { /* noop */ }
              return;
            }
            if (!value || !value.length) continue;
            await new Promise((res2, rej2) => {
              const onUpdateEnd = () => {
                sourceBuffer.removeEventListener('updateend', onUpdateEnd);
                sourceBuffer.removeEventListener('error', onErr);
                res2();
              };
              const onErr = (e) => {
                sourceBuffer.removeEventListener('updateend', onUpdateEnd);
                sourceBuffer.removeEventListener('error', onErr);
                rej2(e);
              };
              sourceBuffer.addEventListener('updateend', onUpdateEnd);
              sourceBuffer.addEventListener('error', onErr);
              try {
                sourceBuffer.appendBuffer(value);
              } catch (appendErr) {
                sourceBuffer.removeEventListener('updateend', onUpdateEnd);
                sourceBuffer.removeEventListener('error', onErr);
                rej2(appendErr);
              }
            });
          }
        };
        pump().catch((pumpErr) => {
          console.warn('[voicePlayback] streaming pump error:', pumpErr?.message || pumpErr);
        });
        // Kick off playback once the SourceBuffer exists — Audio element
        // will buffer briefly then start as soon as it has enough data.
        audio.play().then(resolve).catch(reject);
      } catch (err) {
        reject(err);
      }
    };
    mediaSource.addEventListener('sourceopen', onSourceOpen, { once: true });
    // Watchdog: if sourceopen never fires within 5s, give up.
    setTimeout(() => reject(new Error('MediaSource sourceopen timed out')), 5000);
  });
}

/** Stop any in-progress TTS playback / in-flight request. Safe to call anytime. */
export function stopText() {
  try { currentAudio?.pause(); } catch (_) { /* noop */ }
  if (currentAudio) {
    try { currentAudio.src = ''; } catch (_) { /* noop */ }
  }
  currentAudio = null;
  if (currentMediaSource) {
    try {
      if (currentMediaSource.readyState === 'open') currentMediaSource.endOfStream();
    } catch (_) { /* noop */ }
    currentMediaSource = null;
  }
  if (currentUrl) {
    try { URL.revokeObjectURL(currentUrl); } catch (_) { /* noop */ }
    currentUrl = null;
  }
  try { currentAbort?.abort(); } catch (_) { /* noop */ }
  currentAbort = null;
}
